import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent, HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

import { ExecutionStatus } from '../../proxy/models/common';
import { LoadingService } from '../services/loading.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CLIENT_ID } from '../global/config';
import { SUPPRESS_ERROR_TOAST } from '../global/http-context';

export const httpRequestInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const toast = inject(ToastrService);
  const loadingService = inject(LoadingService);
  const oidc = inject(OidcSecurityService);
  const clientId = inject(CLIENT_ID);

  return next(req).pipe(
    map((event: HttpEvent<any>) => {
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      const result = event.body?.result;
      if (!result) return event;

      const { status, errors } = result;
      const firstError = errors?.[0];
      const errorNumber = firstError?.errorNumber;
      const message = firstError?.message;

      if (status === ExecutionStatus.failure) {
        if ([1004, 1005, 1006].includes(errorNumber)) {
          loadingService.hideLoader();
          oidc.logoff(clientId, { customParams: { reason: 'expired' } })?.subscribe();
          return event;
        }

        if (errorNumber === -1830258777 || errorNumber === 66601) {
          router.navigate(['/']);
          return event;
        }
      }

      if (status === ExecutionStatus.warning && message) {
        toast.warning(message, '', { timeOut: 5000 });
      }

      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        oidc.logoff(clientId, { customParams: { reason: 'expired' } })?.subscribe();
        return throwError(() => error);
      }

      if (error.status === 403) {
        router.navigate(['/forbidden']);
        return throwError(() => error);
      }

      // Background callers report their own failures, once. Without this a fan-out of N
      // background requests turns a single outage into N toasts, and a retry doubles it.
      if (!req.context.get(SUPPRESS_ERROR_TOAST)) {
        if (error.status >= 500) {
          toast.error('A server error occurred. Please try again later.', '', { timeOut: 5000 });
        } else if (error.status === 0) {
          toast.error('Network error. Please check your connection.', '', { timeOut: 5000 });
        }
      }

      return throwError(() => error);
    })
  );
};
